import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import AdminPanel from './pages/AdminPanel';
import RegisterPage from './pages/RegisterPage';
import LoginPage from './pages/LoginPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardNew from './pages/DashboardNew';
import LiveCoaching from './pages/LiveCoachingRouter';
import SessionArchive from './pages/SessionArchive';
import SearchPage from './pages/SearchPage';
import LearnMode from './pages/LearnMode';
import LearnTopic from './pages/LearnTopic';
import AuthCallback from './pages/AuthCallback';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import OuraCallback from './pages/OuraCallback';
import BetaWelcomePage from './pages/BetaWelcomePage';
import { Toaster } from './components/ui/sonner';
import { TutorialProvider } from './components/tutorial/TutorialContext';
import TutorialOverlay from './components/tutorial/TutorialOverlay';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

/**
 * AppRouter — intercepts the OAuth callback fragment (`#session_id=...`)
 * SYNCHRONOUSLY during render and hands it to AuthCallback before any
 * protected route can run its own auth check. This ordering is critical:
 * doing it in useEffect would let Dashboard's fetch hit /api/auth/me first
 * with no JWT and redirect the user to /login mid-exchange (a race).
 */
function AppRouter() {
  const location = useLocation();
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  if (location.pathname === '/dashboard' && /[?&]state=/.test(location.search) && /[?&](code|error)=/.test(location.search)) {
    return <OuraCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/admin" element={<AdminPanel />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/dashboard" element={<DashboardNew />} />
      <Route path="/live-coaching" element={<LiveCoaching />} />
      <Route path="/sessions" element={<SessionArchive />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/learn" element={<LearnMode />} />
      <Route path="/learn/:topicId" element={<LearnTopic />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/oura/callback" element={<OuraCallback />} />
      <Route path="/welcome" element={<BetaWelcomePage />} />
    </Routes>
  );
}

function AppThemedToaster() {
  // Sonner needs to know the theme so toast chrome stays legible.
  const { theme } = useTheme();
  return <Toaster position="top-center" theme={theme} />;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <TutorialProvider>
            <AppRouter />
            <TutorialOverlay />
          </TutorialProvider>
        </BrowserRouter>
        <AppThemedToaster />
      </ThemeProvider>
    </div>
  );
}

export default App;
